import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/breadcrumb";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useAdminApiClient } from "../../hooks/useApiClient";
import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Image,
  Skeleton,
  SkeletonText,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogCloseButton,
  AlertDialogBody,
  FormControl,
  FormLabel,
  Input,
  AlertDialogFooter,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  ButtonGroup,
} from "@chakra-ui/react";
import { EditIcon, EllipsisIcon } from "../../styles/icons";
import { useToast } from "../../hooks/useToast";
import { UserDetailForm } from "../../forms/UserDetailForm";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import DateUtil from "../../util/DateUtil";
import { NewUserForm } from "../../forms/NewUserForm";
import { useAdminApiUpdateRequest } from "../../hooks/useApiUpdatedRequest";
import {
  CheckCircleIcon,
  DeleteIcon,
  EmailIcon,
  NotAllowedIcon,
} from "@chakra-ui/icons";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { cloneDeep } from "lodash";
import { CopyText } from "../../components/Utilities/CopyText";
import { notJson } from "../../util/stringHelper";
import { ViewControl } from "../../components/ViewControl/ViewControl";

interface UserDetailProps {
  create?: boolean;
}

export const UserDetail = (props: UserDetailProps) => {
  const [searchParams] = useSearchParams();
  const { uid } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<CherryHubAdminApi.UserAccount>();
  const apiClient = useAdminApiClient();
  const { showErrorToast, showSuccessToast } = useToast();
  const [editMode, setEditMode] = useState<boolean>(
    props.create ??
      (searchParams.get("edit") !== null &&
      (searchParams.get("edit") === "" || searchParams.get("edit") == "true")
        ? true
        : false)
  );

  const {
    confirmationElement: enableDisableConfirmationElement,
    showConfirmationDialog: enableDisableShowConfirmationDialog,
  } = useConfirmationDialog();

  const { deleteConfirmationElement, deleteShowConfirmationDialog } =
    useDeleteConfirmationDialog();

  const { data: userData, isLoading } = useAdminApiRequest(
    (adminApiClient) => adminApiClient.getUser(uid!),
    [uid]
  );

  useEffect(() => {
    setUser(userData!);
  }, [userData]);

  const onSuccess = useCallback(
    (user: CherryHubAdminApi.UserAccount) => {
      setUser(user);
      setEditMode(false);
      if (props.create) {
        showSuccessToast("User created.");
        navigate(`/admin/users/${user.Uid ?? uid}`);
      } else {
        showSuccessToast("User updated.");
      }
    },
    [showSuccessToast, user, setUser, setEditMode, props.create, navigate]
  );

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "User update failed.");
    },
    [showErrorToast]
  );

  const deleteUserRequest = useAdminApiUpdateRequest(
    (apiClient, userId: string, email: string) =>
      apiClient.deleteUser(userId, { id: email }),
    [user]
  );

  useEffect(() => {
    if (deleteUserRequest.result) {
      if (deleteUserRequest.result.ok) {
        showSuccessToast("User deleted.");
        navigate("/admin/users");
      } else {
        showErrorToast(
          deleteUserRequest.result.message ?? "Failed to delete user."
        );
      }
    }
  }, [deleteUserRequest.result]);

  const onClickDeleteUser = useCallback(
    (user: CherryHubAdminApi.UserAccount) =>
      deleteShowConfirmationDialog(
        "Delete User?",
        `Are you sure you wish to delete this user: ${
          user.DisplayName ?? user.Email ?? user.Uid
        }?`,
        "Delete"
      ).then(({ ok, id }) => {
        if (ok) {
          deleteUserRequest.request(user.Uid!, id!);
        }
      }),
    [deleteShowConfirmationDialog]
  );

  const disableUserRequest = useAdminApiUpdateRequest(
    (apiClient, userId: string) => apiClient.disableUser(userId),
    [user]
  );

  const enableUserRequest = useAdminApiUpdateRequest(
    (apiClient, userId: string) => apiClient.enableUser(userId),
    [user]
  );

  useEffect(() => {
    if (disableUserRequest.result) {
      if (disableUserRequest.result.ok) {
        showSuccessToast("User disabled.");
        const userData = cloneDeep(user);
        userData!.Disabled = true;
        setUser(userData!);
      } else {
        showErrorToast(
          disableUserRequest.result.message ?? "Failed to disable user."
        );
      }
    }
  }, [disableUserRequest.result]);

  useEffect(() => {
    if (enableUserRequest.result) {
      if (enableUserRequest.result.ok) {
        showSuccessToast("User enabled.");
        const userData = cloneDeep(user);
        userData!.Disabled = false;
        setUser(userData!);
      } else {
        showErrorToast(
          enableUserRequest.result.message ?? "Failed to enable user."
        );
      }
    }
  }, [enableUserRequest.result]);

  const onEnableDisableUser = useCallback(
    (user: CherryHubAdminApi.UserAccount) =>
      enableDisableShowConfirmationDialog(
        user.Disabled ? "Enable User?" : "Disable User?",
        `Are you sure you wish to ${
          user.Disabled ? "enable" : "disable"
        } this user: ${user.DisplayName ?? user.Email ?? user.Uid}?`,
        user.Disabled ? "Enable" : "Disable"
      ).then(({ ok }) => {
        if (ok) {
          user.Disabled
            ? enableUserRequest.request(user.Uid!)
            : disableUserRequest.request(user.Uid!);
        }
      }),
    [enableDisableShowConfirmationDialog]
  );

  const resetUserRequest = useAdminApiUpdateRequest(
    (apiClient, userId: string) => apiClient.resetUserPassword(userId),
    [user]
  );

  useEffect(() => {
    if (resetUserRequest.result) {
      if (resetUserRequest.result.ok) {
        showSuccessToast("Reset password success.");
      } else {
        showErrorToast(
          resetUserRequest.result.message ?? "Failed to reset password user."
        );
      }
    }
  }, [resetUserRequest.result, showSuccessToast, showErrorToast]);

  const onResetPasswordUser = useCallback(
    (user: CherryHubAdminApi.UserAccount) =>
      enableDisableShowConfirmationDialog(
        "Send Reset User Password?",
        `Are you sure you wish to reset this user: ${
          user.DisplayName ?? user.Email ?? user.Uid
        } password?`,
        "Send Reset Password"
      ).then(({ ok }) => {
        if (ok) {
          resetUserRequest.request(user.Uid!);
        }
      }),
    [enableDisableShowConfirmationDialog]
  );

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          {props.create ? "Create" : "Edit"} User
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <Breadcrumb variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to={`/admin/users`}>
                Users
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/users/${props.create ? "new" : uid}`}
              >
                {props.create ? "New" : uid}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
          {((isLoading || !user) && !props.create && (
            <VStack
              w="100%"
              p={6}
              boxShadow="lg"
              alignItems="flex-start"
              spacing={4}
            >
              <HStack w="100%" justifyContent="space-between">
                <Skeleton w="320px" height="40px" />
                <Skeleton w="80px" height="40px" />
              </HStack>
              <SkeletonText w="50%" mt="4" noOfLines={10} spacing="4" />
            </VStack>
          )) ||
            (editMode ? (
              props.create ? (
                <NewUserForm
                  apiClient={apiClient}
                  onCancel={() => navigate("/admin/users")}
                  onSuccess={onSuccess}
                  onFailure={onFailure}
                />
              ) : (
                <UserDetailForm
                  apiClient={apiClient}
                  user={user!}
                  onCancel={(user) =>
                    user ? onSuccess(user) : setEditMode(false)
                  }
                  onSuccess={onSuccess}
                  onFailure={onFailure}
                />
              )
            ) : (
              <>
                <HStack w="100%" pb={4}>
                  <Box flexGrow={1}>
                    <CopyText text={user?.Uid} hover adjustMargin>
                      <Text fontSize="md" color="cherryUi.600">
                        {user?.Uid}
                      </Text>
                    </CopyText>
                    <Text fontSize="xl" color="cherryUi.600">
                      <b>
                        {user?.DisplayName ??
                          user?.Email ??
                          user?.PhoneNumber ??
                          "Unknown"}
                      </b>
                    </Text>
                  </Box>
                  <Box>
                    <ButtonGroup isAttached>
                      <Button
                        leftIcon={<EditIcon />}
                        onClick={() => setEditMode(true)}
                        colorScheme="cherryButton"
                        borderRight="1px"
                      >
                        Edit
                      </Button>

                      <Menu size="sm">
                        <MenuButton
                          as={IconButton}
                          colorScheme="cherryButton"
                          aria-label="Options"
                          title="Options"
                          icon={<EllipsisIcon />}
                        />
                        <MenuList>
                          {user?.Disabled ? (
                            <MenuItem
                              icon={<CheckCircleIcon />}
                              onClick={() => onEnableDisableUser(user!)}
                              fontSize="sm"
                            >
                              Enable
                            </MenuItem>
                          ) : (
                            <MenuItem
                              icon={<NotAllowedIcon />}
                              onClick={() => onEnableDisableUser(user!)}
                              fontSize="sm"
                            >
                              Disable
                            </MenuItem>
                          )}
                          <MenuItem
                            icon={<EmailIcon />}
                            onClick={() => onResetPasswordUser(user!)}
                            fontSize="sm"
                          >
                            Reset Password
                          </MenuItem>
                          <MenuItem
                            icon={<DeleteIcon />}
                            onClick={() => onClickDeleteUser(user!)}
                            fontSize="sm"
                          >
                            Delete User
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </ButtonGroup>
                  </Box>
                </HStack>

                <HStack w="100%" pb={6}>
                  <VStack alignItems="flex-start" w="100%">
                    <ViewControl
                      label="Display Name"
                      value={user?.DisplayName ?? "-"}
                    />

                    <ViewControl
                      label="Enabled"
                      value={user?.Disabled ? "No" : "Yes"}
                    />

                    <ViewControl label="Email">
                      <Box flex={3}>
                        {(user?.Email && (
                          <CopyText adjustMargin>{user?.Email}</CopyText>
                        )) ||
                          "-"}
                      </Box>
                    </ViewControl>

                    <ViewControl label="Phone Number">
                      <Box flex={3}>
                        {(user?.PhoneNumber && (
                          <CopyText adjustMargin>{user?.PhoneNumber}</CopyText>
                        )) ||
                          "-"}
                      </Box>
                    </ViewControl>

                    <ViewControl label="Provider Id" value={user?.ProviderId} />

                    <ViewControl
                      label="Email Verified"
                      value={user?.EmailVerified ? "Yes" : "No"}
                    />

                    <ViewControl
                      label="Tokens Valid After Timestamp"
                      value={DateUtil.convertUtcToLocalTime(
                        user?.TokensValidAfterTimestamp
                      )}
                    />

                    <ViewControl label="Custom Claims">
                      <UnorderedList flex={3} mx={0}>
                        {(user?.CustomClaims &&
                          Object.entries(user?.CustomClaims).length &&
                          Object.entries(user?.CustomClaims)?.map(
                            ([key, value]) => {
                              return (
                                <ListItem ml={4} key={key}>
                                  {key} -{" "}
                                  {notJson(JSON.stringify(value))
                                    ? (value as string)
                                    : JSON.stringify(value)}
                                </ListItem>
                              );
                            }
                          )) || (
                          <Text fontStyle="italic">
                            Custom Claims has not been set.
                          </Text>
                        )}
                      </UnorderedList>
                    </ViewControl>
                  </VStack>

                  {user?.PhotoUrl && (
                    <Box alignSelf="flex-start">
                      <Image
                        border="double 1px grey"
                        maxHeight="200px"
                        objectFit="contain"
                        src={user?.PhotoUrl}
                      />
                    </Box>
                  )}
                </HStack>
              </>
            ))}
        </VStack>
        {deleteConfirmationElement}
        {enableDisableConfirmationElement}
      </PageContent>
    </>
  );
};

interface PromptState {
  resolve: (result: { ok: boolean; id?: string }) => void;
  reject: () => void;
  title: string;
  message: JSX.Element | string;
  action: string;
}

export function useDeleteConfirmationDialog() {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [activePrompt, setActivePrompt] = useState<PromptState | null>(null);
  const [id, setId] = useState("");

  const onClickOk = useCallback(() => {
    if (activePrompt) {
      activePrompt.resolve({ ok: true, id });
      setActivePrompt(null);
    }
  }, [activePrompt, setActivePrompt, id]);

  const onCancel = useCallback(() => {
    if (activePrompt) {
      activePrompt.resolve({ ok: false });
      setActivePrompt(null);
    }
  }, [activePrompt, setActivePrompt, id]);

  const confirmationElement = useMemo(() => {
    return (
      <AlertDialog
        isOpen={!!activePrompt}
        onClose={onCancel}
        leastDestructiveRef={cancelRef}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>{activePrompt?.title ?? "-"}</AlertDialogHeader>
            <AlertDialogCloseButton />
            <AlertDialogBody>
              <FormControl>
                <Text>{activePrompt?.message ?? "-"}</Text>
                <br />
                <FormLabel>Please confirm your Email or Phone Number</FormLabel>
                <Input
                  placeholder="Email or Phone Number"
                  onChange={(e) => setId(e.target.value)}
                />
              </FormControl>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCancel}>
                Cancel
              </Button>
              <Button
                colorScheme="cherryButton"
                ml={3}
                onClick={onClickOk}
                isDisabled={id == null || id == ""}
              >
                {activePrompt?.action ?? "Yes"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    );
  }, [activePrompt, cancelRef, onCancel, onClickOk, id]);

  const showConfirmationDialog = useCallback(
    (
      title: string,
      message: JSX.Element | string,
      action: string
    ): Promise<{ ok: boolean; id?: string }> => {
      const promise = new Promise<{ ok: boolean; id?: string }>(
        (resolve, reject) => {
          setActivePrompt({
            resolve,
            reject,
            title,
            message,
            action,
          });

          setId("");
        }
      ).then((result) => {
        return result;
      });

      return promise;
    },
    [setActivePrompt, id]
  );

  return {
    deleteConfirmationElement: confirmationElement,
    deleteShowConfirmationDialog: showConfirmationDialog,
  };
}
